import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Building2, Landmark, UserRound, Calculator, Loader2, MapPin, Clock, Share2, ImagePlus, Plus, Trash2, X, Check, ChevronsUpDown, ShieldOff, Snowflake, CheckCircle, AlertTriangle, Smartphone, type LucideIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useMerchantId } from '@/hooks/useMerchantId';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/lib/api-error';


const DAYS = [
  { key: 'mon', label: 'cust_br.day_mon' },
  { key: 'tue', label: 'cust_br.day_tue' },
  { key: 'wed', label: 'cust_br.day_wed' },
  { key: 'thu', label: 'cust_br.day_thu' },
  { key: 'fri', label: 'cust_br.day_fri' },
  { key: 'sat', label: 'cust_br.day_sat' },
  { key: 'sun', label: 'cust_br.day_sun' },
];

export default function CompanyTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { merchantId } = useMerchantId();
  const merchantIdStr = merchantId != null ? String(merchantId) : null;
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const { data: merchantResp, isLoading } = useQuery({ queryKey: ['/merchant'] });
  const merchant = merchantResp?.data;

  // Business categories (already added to this merchant)
  const { data: bizCategoriesResp } = useQuery({ queryKey: ['/categories'] });
  const bizCategories = bizCategoriesResp?.data ?? [];

  // All distinct product categories (from products table)
  const { data: productsResp } = useQuery({ queryKey: ['/products'] });
  const allProductCategories = [...new Set(
    (productsResp?.data ?? []).map((p) => p.category).filter((c): c is string => !!c)
  )].sort();

  const [catPickerOpen, setCatPickerOpen] = useState(false);

  const [form, setForm] = useState({
    name: '', legal_name: '', inn: '', phone: '', address: '',
    bank_account: '', bank_name: '', bank_code: '',
    director_name: '', director_passport: '', director_phone: '',
    accountant_name: '', accountant_passport: '', accountant_phone: '',
    oked: '', vat_reg_code: '', vat_mode: 'vat_payer' as 'vat_payer' | 'exempt', vat_privilege_code: '',
    director_pinfl: '', accountant_pinfl: '',
    latitude: '', longitude: '', logo_url: '',
    social_instagram: '', social_telegram: '', social_facebook: '', website: '',
    description_short: '', description_full: '', cover_image_url: '', card_image_url: '',
    is_visible_in_app: true,
    working_hours: {} as Record<string, string>,
  });

  useEffect(() => {
    if (merchant) {
      setForm({
        name: merchant.name || '',
        legal_name: merchant.legal_name || '',
        inn: merchant.inn || '',
        phone: merchant.phone || '',
        address: merchant.address || '',
        bank_account: merchant.bank_account || '',
        bank_name: merchant.bank_name || '',
        bank_code: merchant.bank_code || '',
        director_name: merchant.director_name || '',
        director_passport: merchant.director_passport || '',
        director_phone: merchant.director_phone || '',
        accountant_name: merchant.accountant_name || '',
        accountant_passport: merchant.accountant_passport || '',
        accountant_phone: merchant.accountant_phone || '',
        oked: merchant.oked || '',
        vat_reg_code: merchant.vat_reg_code || '',
        vat_mode: merchant.vat_mode || 'vat_payer',
        vat_privilege_code: merchant.vat_privilege_code || '',
        director_pinfl: merchant.director_pinfl || '',
        accountant_pinfl: merchant.accountant_pinfl || '',
        latitude: merchant.latitude?.toString() || '',
        longitude: merchant.longitude?.toString() || '',
        logo_url: merchant.logo_url || '',
        social_instagram: merchant.social_instagram || '',
        social_telegram: merchant.social_telegram || '',
        social_facebook: merchant.social_facebook || '',
        website: merchant.website || '',
        description_short: merchant.description_short || '',
        description_full: merchant.description_full || '',
        cover_image_url: merchant.cover_image_url || '',
        card_image_url: merchant.card_image_url || '',
        is_visible_in_app: merchant.is_visible_in_app ?? true,
        working_hours: (merchant.working_hours as Record<string, string>) || {},
      });
    }
  }, [merchant]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleWorkingHoursChange = (day: string, value: string) => {
    setForm(prev => ({ ...prev, working_hours: { ...prev.working_hours, [day]: value } }));
  };


  const openGoogleMap = () => {
    const lat = form.latitude || '41.2995';
    const lng = form.longitude || '69.2401';
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const handleSave = async () => {
    if (!merchant?.id) return;
    if (!form.name.trim()) { toast.error(t('cust_co.name_required')); return; }
    if (!form.legal_name.trim()) { toast.error(t('cust_co.legal_name_required')); return; }
    if (form.bank_account && form.bank_account.replace(/\s/g, '').length !== 20 && form.bank_account.trim() !== '') {
      toast.error(t('cust_co.account_20')); return;
    }
    if (form.oked && !/^\d{5}$/.test(form.oked)) { toast.error(t('company_tax.errors.oked')); return; }
    if (form.vat_reg_code && !/^\d{12}$/.test(form.vat_reg_code)) { toast.error(t('company_tax.errors.vat_reg_code')); return; }
    if (form.director_pinfl && !/^\d{14}$/.test(form.director_pinfl)) { toast.error(t('company_tax.errors.director_pinfl')); return; }
    if (form.accountant_pinfl && !/^\d{14}$/.test(form.accountant_pinfl)) { toast.error(t('company_tax.errors.accountant_pinfl')); return; }
    if (form.vat_mode === 'exempt' && !form.vat_privilege_code.trim()) { toast.error(t('company_tax.errors.privilege_code')); return; }

    setSaving(true);
    try {
      // logo_url/cover_image_url/card_image_url are written only by
      // MediaUploadField (POST /merchant/media/{kind}) — UpdateMerchantRequest
      // rejects them here on purpose (ARCHITECTURE.md: free-text *_url on this
      // endpoint was a Supabase-prototype bug, not carried over).
      const { error } = await apiClient.PATCH('/merchant', {
        body: {
          name: form.name.trim(),
          legal_name: form.legal_name.trim(),
          inn: form.inn.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          bank_account: form.bank_account.trim(),
          bank_name: form.bank_name.trim(),
          bank_code: form.bank_code.trim(),
          director_name: form.director_name.trim(),
          director_passport: form.director_passport.trim(),
          director_phone: form.director_phone.trim(),
          accountant_name: form.accountant_name.trim(),
          accountant_passport: form.accountant_passport.trim(),
          accountant_phone: form.accountant_phone.trim(),
          oked: form.oked,
          vat_reg_code: form.vat_reg_code,
          vat_mode: form.vat_mode,
          vat_rate: form.vat_mode === 'vat_payer' ? 12 : 0,
          vat_privilege_code: form.vat_mode === 'exempt' ? form.vat_privilege_code.trim() : '',
          director_pinfl: form.director_pinfl,
          accountant_pinfl: form.accountant_pinfl,
          latitude: form.latitude ? parseFloat(form.latitude) : null,
          longitude: form.longitude ? parseFloat(form.longitude) : null,
          social_instagram: form.social_instagram.trim(),
          social_telegram: form.social_telegram.trim(),
          social_facebook: form.social_facebook.trim(),
          website: form.website.trim(),
          description_short: form.description_short.trim(),
          description_full: form.description_full.trim(),
          is_visible_in_app: form.is_visible_in_app,
          working_hours: form.working_hours,
        },
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_co.save_error'));
      toast.success(t('cust_co.saved'));
      queryClient.invalidateQueries({ queryKey: ['/merchant'] });
    } catch (err) {
      toast.error(apiErrorMessage(err, t('cust_co.save_error')));
    } finally {
      setSaving(false);
    }
  };

  // Add category from existing product categories
  const addCatMutation = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await apiClient.POST('/categories', { body: { name: name.trim() } });
      if (error) throw new Error('message' in error ? String(error.message) : 'Xatolik');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/categories'] });
      toast.success(t('cust_co.category_added'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteCatMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/categories/{category}', {
        params: { path: { category: id } },
      });
      if (error) throw new Error('message' in error ? String(error.message) : 'Xatolik');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/categories'] });
      toast.success(t('cust_co.category_deleted'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Categories already added (names)
  const addedCatNames = bizCategories.map((c) => c.name);
  // Available to add (not yet added)
  const availableCategories = allProductCategories.filter((name: string) => !addedCatNames.includes(name));

  if (isLoading) {
    return <div className="py-6 text-center text-muted-foreground">{t('cust_common.loading')}</div>;
  }

  const merchantPlatformStatus = user?.merchant_platform_status;

  const statusConfig: Record<string, { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline'; icon: LucideIcon; description: string }> = {
    active: { label: t('cust_co.st_active'), variant: 'default', icon: CheckCircle, description: '' },
    blocked: { label: t('cust_co.st_blocked'), variant: 'destructive', icon: ShieldOff, description: t('cust_co.st_blocked_desc') },
    frozen: { label: t('cust_co.st_frozen'), variant: 'secondary', icon: Snowflake, description: t('cust_co.st_frozen_desc') },
    new: { label: t('cust_co.st_new'), variant: 'outline', icon: Clock, description: t('cust_co.st_review_desc') },
    pending: { label: t('cust_co.st_pending'), variant: 'outline', icon: Clock, description: t('cust_co.st_review_desc') },
    rejected: { label: t('cust_co.st_rejected'), variant: 'destructive', icon: AlertTriangle, description: t('cust_co.st_rejected_desc') },
  };

  const currentStatus = merchantPlatformStatus ? statusConfig[merchantPlatformStatus] : null;

  return (
    <div className="space-y-6">
      {/* Merchant Platform Status Banner */}
      {currentStatus && merchantPlatformStatus !== 'active' && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${
          merchantPlatformStatus === 'blocked' ? 'border-destructive/30 bg-destructive/5' :
          merchantPlatformStatus === 'frozen' ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800' :
          'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800'
        }`}>
          <currentStatus.icon size={20} className={`shrink-0 mt-0.5 ${
            merchantPlatformStatus === 'blocked' ? 'text-destructive' :
            merchantPlatformStatus === 'frozen' ? 'text-blue-600' :
            'text-yellow-600'
          }`} />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-sm text-foreground">{t('cust_co.company_status')}</span>
              <Badge variant={currentStatus.variant}>{currentStatus.label}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{currentStatus.description}</p>
            {user?.merchant_admin_comment && (
              <p className="text-xs text-muted-foreground mt-1"><strong>{t('cust_co.admin_comment')}</strong> {user.merchant_admin_comment}</p>
            )}
          </div>
        </div>
      )}

      {/* Active status badge inline */}
      {merchantPlatformStatus === 'active' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('cust_co.company_status')}</span>
          <Badge variant="default" className="bg-green-600"><CheckCircle size={12} className="mr-1" /> {t('cust_co.st_active')}</Badge>
        </div>
      )}
      {/* Logo */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <ImagePlus size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.logo')}</h2>
        </div>
        <MediaUploadField
          kind="merchant_logo"
          targetId={merchantIdStr}
          value={form.logo_url}
          onChange={(url) => setForm(prev => ({ ...prev, logo_url: url }))}
          label={t('cust_co.logo')}
          shape="square"
        />
      </div>

      {/* Asosiy ma'lumotlar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.basic_info')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.company_name')}</label>
            <Input value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder={t('cust_co.company_name')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.legal_name')}</label>
            <Input value={form.legal_name} onChange={e => handleChange('legal_name', e.target.value)} placeholder={t('cust_co.legal_name')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.inn')}</label>
            <Input value={form.inn} onChange={e => handleChange('inn', e.target.value)} placeholder="123456789" maxLength={9} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.phone')}</label>
            <Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+998..." />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.address')}</label>
          <Input value={form.address} onChange={e => handleChange('address', e.target.value)} placeholder={t('cust_co.address')} />
        </div>
      </div>

      {/* Lokatsiya */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.location')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.latitude')}</label>
            <Input value={form.latitude} onChange={e => handleChange('latitude', e.target.value)} placeholder="41.2995" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.longitude')}</label>
            <Input value={form.longitude} onChange={e => handleChange('longitude', e.target.value)} placeholder="69.2401" />
          </div>
        </div>
        <Button size="sm" variant="outline" className="mt-3" onClick={openGoogleMap}>
          <MapPin size={14} className="mr-1" /> {t('cust_co.open_gmaps')}
        </Button>
      </div>

      {/* Kategoriyalar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">{t('cust_co.categories')}</h2>
          </div>
          <Popover open={catPickerOpen} onOpenChange={setCatPickerOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" disabled={availableCategories.length === 0}>
                <Plus size={14} className="mr-1" /> {t('cust_co.choose')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="end">
              <Command>
                <CommandInput placeholder={t('cust_co.search_category')} />
                <CommandList>
                  <CommandEmpty>{t('cust_co.no_more_categories')}</CommandEmpty>
                  <CommandGroup>
                    {availableCategories.map((name: string) => (
                      <CommandItem
                        key={name}
                        value={name}
                        onSelect={() => {
                          addCatMutation.mutate(name);
                          setCatPickerOpen(false);
                        }}
                      >
                        <Plus size={14} className="mr-2 text-muted-foreground" />
                        {name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {availableCategories.length === 0 && bizCategories.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('cust_co.set_product_categories')}</p>
        )}
        {bizCategories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {bizCategories.map((c) => (
              <Badge key={c.id} variant="secondary" className="text-xs px-3 py-1.5 gap-2">
                {c.name}
                <button onClick={() => deleteCatMutation.mutate(c.id)} className="hover:text-destructive">
                  <X size={12} />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Bank rekvizitlari */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Landmark size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.bank_details')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.account_number')}</label>
            <Input
              value={form.bank_account}
              onChange={e => { const val = e.target.value.replace(/[^0-9]/g, ''); if (val.length <= 20) handleChange('bank_account', val); }}
              placeholder="00000000000000000000" maxLength={20} className="font-mono tracking-wider"
            />
            {form.bank_account && form.bank_account.length !== 20 && (
              <p className="text-xs text-warning mt-1">{t('cust_co.digits_entered', { n: form.bank_account.length })}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.bank_name')}</label>
            <Input value={form.bank_name} onChange={e => handleChange('bank_name', e.target.value)} placeholder={t('cust_co.bank_name')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.bank_code')}</label>
            <Input value={form.bank_code} onChange={e => { const val = e.target.value.replace(/[^0-9]/g, ''); if (val.length <= 5) handleChange('bank_code', val); }} placeholder="00000" maxLength={5} className="font-mono" />
          </div>
        </div>
      </div>

      {/* Direktor */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <UserRound size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.director')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.person_name')}</label>
            <Input value={form.director_name} onChange={e => handleChange('director_name', e.target.value)} placeholder={t('cust_co.person_name_ph')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.passport_series')}</label>
            <Input value={form.director_passport} onChange={e => handleChange('director_passport', e.target.value.toUpperCase())} placeholder="AA 1234567" maxLength={10} className="font-mono uppercase" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.phone_number')}</label>
            <Input value={form.director_phone} onChange={e => handleChange('director_phone', e.target.value)} placeholder="+998..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.pinfl')} <span className="text-destructive">*</span></label>
            <Input value={form.director_pinfl} onChange={e => handleChange('director_pinfl', e.target.value.replace(/[^0-9]/g, '').slice(0, 14))} placeholder={t('company_tax.pinfl_placeholder')} className="font-mono" />
          </div>
        </div>
      </div>

      {/* Buxgalter */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calculator size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.accountant')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.person_name')}</label>
            <Input value={form.accountant_name} onChange={e => handleChange('accountant_name', e.target.value)} placeholder={t('cust_co.person_name_ph')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.passport_series')}</label>
            <Input value={form.accountant_passport} onChange={e => handleChange('accountant_passport', e.target.value.toUpperCase())} placeholder="AA 1234567" maxLength={10} className="font-mono uppercase" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.phone_number')}</label>
            <Input value={form.accountant_phone} onChange={e => handleChange('accountant_phone', e.target.value)} placeholder="+998..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.pinfl')} </label>
            <Input value={form.accountant_pinfl} onChange={e => handleChange('accountant_pinfl', e.target.value.replace(/[^0-9]/g, '').slice(0, 14))} placeholder={t('company_tax.pinfl_placeholder')} className="font-mono" />
          </div>
        </div>
      </div>

      {/* Soliq rekvizitlari */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Calculator size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('company_tax.title')}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('company_tax.hint')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.oked')} <span className="text-destructive">*</span></label>
            <Input value={form.oked} onChange={e => handleChange('oked', e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} placeholder="47190" className="font-mono" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.vat_reg_code')} <span className="text-destructive">*</span></label>
            <Input value={form.vat_reg_code} onChange={e => handleChange('vat_reg_code', e.target.value.replace(/[^0-9]/g, '').slice(0, 12))} placeholder={t('company_tax.vat_reg_code_placeholder')} className="font-mono" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.vat_mode')} <span className="text-destructive">*</span></label>
            <select value={form.vat_mode} onChange={e => handleChange('vat_mode', e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="vat_payer">{t('company_tax.vat_payer')}</option>
              <option value="exempt">{t('company_tax.exempt')}</option>
            </select>
          </div>
          {form.vat_mode === 'exempt' && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('company_tax.privilege_code')} <span className="text-destructive">*</span></label>
              <Input value={form.vat_privilege_code} onChange={e => handleChange('vat_privilege_code', e.target.value.replace(/[^0-9]/g, '').slice(0, 20))} placeholder={t('company_tax.privilege_code')} className="font-mono" />
            </div>
          )}
        </div>
      </div>

      {/* Ishlash vaqti */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.working_hours')}</h2>
        </div>
        <div className="space-y-2">
          {DAYS.map(d => (
            <div key={d.key} className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-24">{t(d.label)}</span>
              <Input
                value={form.working_hours[d.key] || ''}
                onChange={e => handleWorkingHoursChange(d.key, e.target.value)}
                placeholder={t('cust_co.hours_ph')}
                className="max-w-xs text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Ilovada ko'rinishi */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">{t('cust_co.app_visibility')}</h2>
          </div>
          {merchant?.is_verified ? (
            <Badge variant="default" className="bg-green-600">
              <CheckCircle size={12} className="mr-1" /> {t('cust_co.verified')}
            </Badge>
          ) : (
            <Badge variant="outline">{t('cust_co.not_verified')}</Badge>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.short_desc')}</label>
            <Input
              value={form.description_short}
              onChange={e => handleChange('description_short', e.target.value)}
              placeholder={t('cust_co.short_desc_ph')}
              maxLength={160}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.full_desc')}</label>
            <Textarea
              rows={4}
              value={form.description_full}
              onChange={e => handleChange('description_full', e.target.value)}
              placeholder={t('cust_co.full_desc_ph')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MediaUploadField
              kind="merchant_cover"
              targetId={merchantIdStr}
              value={form.cover_image_url}
              onChange={(url) => handleChange('cover_image_url', url)}
              label={t('cust_co.cover_image')}
            />
            <MediaUploadField
              kind="merchant_card"
              targetId={merchantIdStr}
              value={form.card_image_url}
              onChange={(url) => handleChange('card_image_url', url)}
              label={t('cust_co.card_image')}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm text-foreground">{t('cust_co.show_in_app')}</p>
              <p className="text-xs text-muted-foreground">{t('cust_co.show_in_app_hint')}</p>
            </div>
            <Switch
              checked={form.is_visible_in_app}
              onCheckedChange={v => setForm(prev => ({ ...prev, is_visible_in_app: v }))}
            />
          </div>
        </div>
      </div>

      {/* Ijtimoiy tarmoqlar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Share2 size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_co.social')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Instagram</label>
            <Input value={form.social_instagram} onChange={e => handleChange('social_instagram', e.target.value)} placeholder={t('cust_co.handle_or_link')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Telegram</label>
            <Input value={form.social_telegram} onChange={e => handleChange('social_telegram', e.target.value)} placeholder={t('cust_co.handle_or_link')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Facebook</label>
            <Input value={form.social_facebook} onChange={e => handleChange('social_facebook', e.target.value)} placeholder={t('cust_co.link')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_co.website')}</label>
            <Input value={form.website} onChange={e => handleChange('website', e.target.value)} placeholder="https://example.uz" />
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
        {t('cust_co.save')}
      </Button>

    </div>
  );
}
